import { computed, unref } from 'vue';

import { deepEqual, getPropertyFromItem, omit, propsFactory } from '@components/utils';

import type { PropType, ComputedRef } from 'vue';

export type TItemKey = boolean | string | (string | number)[] | ((item: Record<string, any>, fallback?: any) => any);
export interface IInternalItem<T = any> {
  title: string;
  value: any;
  props: {
    [key: string]: any;
    title: string;
    value: any;
  };
  children?: IInternalItem<T>[];
  raw: T;
}
export interface IItemsProps {
  items: any[];
  itemTitle: TItemKey;
  itemValue: TItemKey;
  itemChildren: TItemKey;
  itemProps: TItemKey;
  returnObject: boolean;
}
export interface IUseItems {
  items: ComputedRef<IInternalItem[]>;
  itemsProps: string[];
  transformIn: (value: any[]) => IInternalItem[];
  transformOut: (value: any[]) => any[];
}

export interface IItemSlotProps {
  isActive: boolean;
  isSelected: boolean;
  isIndeterminate: boolean;
  select: (selected: boolean, event?: Event) => void;
}

export const makeItemsProps = propsFactory(
  {
    /**
     * Массив списка элементов
     */
    items: {
      type: Array as PropType<any[]>,
      default: () => []
    },
    /**
     * Ключ для заголовка элемента
     */
    itemTitle: {
      type: [String, Array, Function] as PropType<TItemKey>,
      default: 'title'
    },
    /**
     * Ключ для значения элемента
     */
    itemValue: {
      type: [String, Array, Function] as PropType<TItemKey>,
      default: 'value'
    },
    /**
     * Ключ для дочерних элементов
     */
    itemChildren: {
      type: [Boolean, String, Array, Function] as PropType<TItemKey>,
      default: 'children'
    },
    /**
     * Ключ для дополнительных свойств элемента
     */
    itemProps: {
      type: [Boolean, String, Array, Function] as PropType<TItemKey>,
      default: 'props'
    },
    /**
     * Возвращать объект вместо массива
     */
    returnObject: {
      type: Boolean as PropType<boolean>,
      default: false
    }
  },
  'items'
);

export function useItems(props: IItemsProps): IUseItems {
  const itemsProps = Object.keys(makeItemsProps());
  const items = computed(() => transformItems(props, props.items));
  const hasNullItem = computed(() => unref(items).some((item) => item.value == null));

  function transformIn(value: any[]): IInternalItem[] {
    if (!unref(hasNullItem)) {
      value = value.filter((val) => val !== null);
    }

    return value.map((val) => {
      if (props.returnObject && typeof val === 'string') {
        // String model value means value is a custom input value from combobox
        // Don't look up existing items if the model value is a string
        return transformItem(props, val);
      }

      return unref(items).find((item) => deepEqual(val, item.value)) || transformItem(props, val);
    });
  }

  function transformOut(value: IInternalItem[]): any[] {
    return props.returnObject ? value.map(({ raw }) => raw) : value.map(({ value }) => value);
  }

  return { items, itemsProps, transformIn, transformOut };
}

export function transformItem(props: Omit<IItemsProps, 'items'>, item: any): IInternalItem {
  const title = getPropertyFromItem(item, props.itemTitle, item);
  const value = getPropertyFromItem(item, props.itemValue, title);
  const children = getPropertyFromItem(item, props.itemChildren);
  const itemProps =
    props.itemProps === true
      ? typeof item === 'object' && item != null && !Array.isArray(item)
        ? 'children' in item
          ? omit(item, ['children'])
          : item
        : undefined
      : getPropertyFromItem(item, props.itemProps);

  const _props = {
    title,
    value,
    ...itemProps
  };

  return {
    title: String(_props.title ?? ''),
    value: _props.value,
    props: _props,
    children: Array.isArray(children) ? transformItems(props, children) : undefined,
    raw: item
  };
}

export function transformItems(props: Omit<IItemsProps, 'items'>, items: IItemsProps['items']): IInternalItem[] {
  const array: IInternalItem[] = [];

  for (const item of items) {
    array.push(transformItem(props, item));
  }

  return array;
}
